import { useTranslation } from 'react-i18next';

// Components
import DateRangePickerDefault from '../DateRangePickerDefault/DateRangePickerDefault';
import DateRangePickerFooter from '../../molecules/DateRangePickerFooter/DateRangePickerFooter';
import DateRangePickerTimeRange, {
  TimeInputWrapper,
  TimeInputGroup,
} from '../../molecules/DateRangePickerTimeRange/DateRangePickerTimeRange';
import TimePicker from '../../atoms/TimePicker/TimePicker';
import Button from '../../atoms/Button/Button';
import DateRangePickerSidebar, {
  DateRangePickerSidebarGroup,
} from '../../molecules/DateRangePickerSidebar/DateRangePickerSidebar';
import NavigationButton from '../../molecules/NavigationButton/NavigationButton';
import { createRangeBySpan } from '../../../domain/dateRange/useDateRangeUseCase';

/**
 *
 * @param {object} params
 * @param {import('../../../domain/dateRange/useDateRange').DateRangeHookReturn} params.dateRange
 * @param {import('react-date-range').Range[]} params.ranges
 * @param {ApiManagerDateRangeType} params.type
 * @param {(type: ApiManagerDateRangeType, range: import('react-date-range').Range, selectedDate: Date) => void} params.handleDateRangeMutation
 * @param {Partial<Record<ApiManagerDateRangeType, number>>} [params.numberMonthDisplayByType]
 * @param {Date} [params.selectedDate] - Anchored date to highlight within the selected range
 * @param {boolean} [params.hasTimeRange=true] - Whether to show the time range controls in custom mode
 * @returns {React.ReactElement}
 */
function FilterDateRangePicker(
  {
    dateRange,
    ranges,
    type,
    handleDateRangeMutation,
    numberMonthDisplayByType,
    selectedDate,
    hasTimeRange = true,
  },
) {
  const { t } = useTranslation();

  const {
    ranges: localRanges,
    setRanges: setLocalRanges,
    focus,
    handleSetFocus,
    handleSetRanges,
    dateRangePickerSidebarButtonsList,
    setCalendarOpen,
    isOpenTimeRange,
    setIsOpenTimeRange,
    timeRange,
    setTimeRange,
  } = dateRange;

  return (
    <DateRangePickerDefault
      type={type}
      selectedDate={selectedDate}
      ranges={localRanges}
      handleRangesChange={(e) => {
        if (type === 'custom') {
          // If custom type, the user will use the footer buttons to apply the changes so we just update the local ranges
          handleSetRanges(e, type);
        } else {
          // If the type is not custom, we want to validate the range and close the picker after selection so we need to create the range
          handleDateRangeMutation(
            type,
            createRangeBySpan(e[0].startDate, type, e[0].endDate),
            e[0].startDate,
          );
          setCalendarOpen(false);
        }
      }}
      focus={focus}
      handleFocusChange={(focusArg) => handleSetFocus(focusArg, type)}
      numberMonthDisplayByType={numberMonthDisplayByType}
      sidebar={(
        <DateRangePickerSidebar>
          <DateRangePickerSidebarGroup>
            {dateRangePickerSidebarButtonsList?.length
              ? dateRangePickerSidebarButtonsList.map((button) => (
                <NavigationButton
                  key={button.id}
                  isMenuOpen
                  theme="submenu"
                  id={button.id}
                  onClick={() => {
                    const range = button.onClick();
                    if (type !== 'custom') {
                      handleDateRangeMutation(type, range, new Date());
                      setCalendarOpen(false);
                    }
                  }}
                  label={t(button.label)}
                />
              )) : null}
          </DateRangePickerSidebarGroup>
        </DateRangePickerSidebar>
      )}
    >
      {type === 'custom' ? (
        <DateRangePickerFooter
          actions={(
            <>
              <Button
                color="primary100"
                size="small"
                onClick={() => {
                  setLocalRanges(ranges);
                  setCalendarOpen(false);
                }}
              >
                {t('date.cancel')}
              </Button>
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  handleDateRangeMutation(type, localRanges[0], localRanges[0].endDate);
                  setCalendarOpen(false);
                }}
              >
                {t('date.apply')}
              </Button>
            </>
      )}
          timeRange={type === 'custom' && hasTimeRange ? (
            <DateRangePickerTimeRange>
              <Button
                color="no-background"
                size="small"
                onClick={() => {
                  setIsOpenTimeRange(!isOpenTimeRange);
                  if (!isOpenTimeRange) {
                    setTimeRange({
                      start: '00:00',
                      end: '23:59',
                    });
                  }
                }}
                icon={isOpenTimeRange ? 'refresh' : 'clock'}
                iconPosition="right"
              >
                {isOpenTimeRange
                  ? t('date.clear')
                  : t('date.specifyHours')}
              </Button>

              <TimeInputWrapper isOpen={isOpenTimeRange}>
                <TimeInputGroup>
                  <TimePicker
                    value={timeRange.start}
                    onChange={(time) => setTimeRange({ ...timeRange, start: time })}
                  />
                </TimeInputGroup>
                <TimeInputGroup>
                  <TimePicker
                    value={timeRange.end}
                    onChange={(time) => setTimeRange({ ...timeRange, end: time })}
                  />
                </TimeInputGroup>
              </TimeInputWrapper>
            </DateRangePickerTimeRange>
          ) : null}
        />
      ) : null}
    </DateRangePickerDefault>
  );
}

export default FilterDateRangePicker;
